import { ColumnTakenError, type MatchingState, type SisColumn, type SisDistrict, type SisFeed, type SisField, type SisListRow, type SisMeaning, type SisRanked, type SisValue } from './sisMatchingApi';

export type ListFilter = 'all' | 'attention' | 'confirmed' | 'nofeed';

export type Tone = 'grey' | 'yellow' | 'green';

export type Pill = { tone: Tone; label: string };

export const LIST_FILTERS: { value: ListFilter; label: string }[] = [
  { value: 'all', label: 'All districts' },
  { value: 'attention', label: 'Need attention' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'nofeed', label: 'No feed' },
];

export const TIERS: { tier: SisField['tier']; label: string; name: string }[] = [
  { tier: 'core', label: 'Core', name: 'Core field' },
  { tier: 'helpful', label: 'Helpful', name: 'Helpful field' },
  { tier: 'optional', label: 'Optional', name: 'Optional field' },
];

export const CLOSEST_LIMIT = 3;

export const NOT_IN_SIS = 'Not in this district’s SIS';

export const SAVE_FAILED = 'Couldn’t save. Try again.';

export function saveFailureMessage(error: unknown): string {
  if (error instanceof ColumnTakenError) {
    return `That column is already used by ${error.fieldLabel}. Change ${error.fieldLabel} first, or pick another column.`;
  }
  return SAVE_FAILED;
}

const number = (n: number) => n.toLocaleString('en-US');

export function plural(n: number, one: string, many?: string): string {
  return `${number(n)} ${n === 1 ? one : (many ?? `${one}s`)}`;
}

export function formatShortDate(iso: string | undefined, withYear = false): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
  const day = `${month} ${date.getUTCDate()}`;
  return withYear ? `${day}, ${date.getUTCFullYear()}` : day;
}

export function formatSyncedAt(iso: string | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
  return `${formatShortDate(iso)}, ${time}`;
}

function needsAttention(row: SisListRow): boolean {
  return row.feed !== null && row.matching !== 'confirmed' && row.matching !== 'no_feed';
}

export function filterRows(rows: SisListRow[], query: string, filter: ListFilter): SisListRow[] {
  const q = query.trim().toLowerCase();
  return rows.filter((row) => {
    if (q && !row.districtName.toLowerCase().includes(q)) return false;
    switch (filter) {
      case 'attention':
        return needsAttention(row);
      case 'confirmed':
        return row.feed !== null && row.matching === 'confirmed';
      case 'nofeed':
        return row.feed === null || row.matching === 'no_feed';
      default:
        return true;
    }
  });
}

export function rowCounts(rows: SisListRow[]): { districts: number; withFeed: number; attention: number; confirmed: number; noFeed: number } {
  const withFeed = rows.filter((r) => r.feed !== null && r.matching !== 'no_feed');
  return {
    districts: rows.length,
    withFeed: withFeed.length,
    attention: rows.filter(needsAttention).length,
    confirmed: withFeed.filter((r) => r.matching === 'confirmed').length,
    noFeed: rows.length - withFeed.length,
  };
}

export function feedLine(feed: SisFeed | null, summaryAsOf?: string): string {
  if (!feed) return 'No feed';
  const synced = feed.syncedAt ? `synced ${formatShortDate(feed.syncedAt)}` : 'not synced yet';
  const stale = summaryAsOf ? ` · summary from ${formatShortDate(summaryAsOf)}` : '';
  return `Since ${formatShortDate(feed.boundAt)} · ${synced}${stale}`;
}

export const ID_FIELDS = ['student_id', 'state_student_id'];

export function staleSummaryNote(summaryAsOf: string | undefined): string {
  if (!summaryAsOf) return '';
  return `Columns and values are from the feed as summarised on ${formatShortDate(summaryAsOf, true)}; the current feed has not synced yet.`;
}

export function valuesCell(row: SisListRow): { text: string; sub: string; warn: boolean } {
  if (row.newValues > 0) {
    return { text: plural(row.newValues, 'new value'), sub: `${row.newFields.join(' · ')} · since ${formatShortDate(row.confirmedAt)}`, warn: true };
  }
  if (row.openValues > 0) {
    return { text: plural(row.openValues, 'needs a meaning', 'need a meaning'), sub: row.openFields.join(' · '), warn: true };
  }
  return { text: 'All matched', sub: '', warn: false };
}

export function statusPill(state: MatchingState): Pill {
  switch (state) {
    case 'confirmed':
      return { tone: 'green', label: 'Confirmed' };
    case 'changed':
      return { tone: 'yellow', label: 'Changed since confirmed' };
    default:
      return { tone: 'grey', label: 'Not confirmed' };
  }
}

export function matchingSubLabel(row: Pick<SisListRow, 'matching' | 'confirmedBy' | 'confirmedByName' | 'confirmedAt'>): string {
  if (row.matching === 'not_confirmed' || !row.confirmedAt) return 'Program Evaluation locked';
  return `${row.confirmedByName || row.confirmedBy || ''} · ${formatShortDate(row.confirmedAt)}`;
}

function columnOf(columns: SisColumn[], name: string | undefined): SisColumn | undefined {
  return columns.find((c) => c.name === name);
}

export function fieldSubLabel(field: SisField, columns: SisColumn[]): { text: string; warn: boolean } {
  const column = field.choice.column ?? '';
  switch (field.state) {
    case 'none':
      return { text: NOT_IN_SIS, warn: false };
    case 'empty':
      return { text: `“${column}” is empty`, warn: true };
    case 'nocol':
      return { text: 'No column chosen', warn: true };
    case 'toomany':
      return { text: 'Too many values to match', warn: true };
    case 'open':
      if (field.new > 0) return { text: plural(field.new, 'new value'), warn: true };
      return { text: `${plural(field.open, 'value needs', 'values need')} a meaning`, warn: true };
    default:
      break;
  }
  if (field.kind === 'value') {
    const values = field.values ?? [];
    const short = values.length > 0 && values.length <= 3 && values.every((v) => v.key !== '' && v.value.length <= 3);
    const summary = short ? [...values].map((v) => v.value).sort().reverse().join(' / ') : plural(field.total, 'value');
    return { text: `${column} · ${summary}`, warn: false };
  }
  if (field.kind === 'group') {
    return { text: `${column} · ${plural(columnOf(columns, column)?.distinct ?? 0, 'value')}`, warn: false };
  }
  return { text: column, warn: false };
}

export function fieldPill(field: SisField): Pill {
  switch (field.state) {
    case 'none':
      return { tone: 'grey', label: 'Not in this SIS' };
    case 'empty':
      return { tone: 'yellow', label: 'Column is empty' };
    case 'nocol':
      return { tone: 'yellow', label: 'No column chosen' };
    case 'toomany':
      return { tone: 'yellow', label: 'Pick a column with fewer values' };
    default:
      break;
  }
  if (field.kind === 'value') {
    if (field.new > 0 && field.open > 0) return { tone: 'yellow', label: `${plural(field.new, 'new value needs', 'new values need')} a meaning` };
    if (field.open > 0) return { tone: 'yellow', label: `${field.open} of ${field.total} values need a meaning` };
    return { tone: 'green', label: `All ${plural(field.total, 'value')} ${field.total === 1 ? 'has' : 'have'} a meaning` };
  }
  if (field.kind === 'date') return { tone: 'green', label: 'Read as dates' };
  if (field.kind === 'group') return { tone: 'green', label: 'Grouped as written' };
  return { tone: 'green', label: 'Read as written' };
}

export function closestColumns(ranked: SisRanked[], chosen: string, showAll: boolean): SisRanked[] {
  if (showAll) return ranked;
  const list = ranked.filter((r) => r.rank > 0).slice(0, CLOSEST_LIMIT);
  if (chosen && !list.some((r) => r.column === chosen)) {
    const own = ranked.find((r) => r.column === chosen);
    if (own) return [own, ...list];
  }
  return list;
}

export function columnLine(column: SisColumn): [string, string] {
  if (column.filled === 0) return ['0 students · empty', 'No values in any row'];
  const count = `${plural(column.filled, 'student')} · ${plural(column.distinct, 'value')}`;
  if (column.identity || !column.samples || column.samples.length === 0) return [count, 'Identifying values are not shown'];
  return [count, column.samples.join(' · ') + (column.distinct > column.samples.length ? ' …' : '')];
}

export function dateReadLine(column: SisColumn, fieldKey: string): { ok: boolean; text: string } {
  const asYears = fieldKey === 'el_service_start' && column.ints === column.filled && column.dates === 0;
  const read = asYears ? column.ints : column.dates;
  return { ok: read === column.filled, text: `${number(read)} of ${number(column.filled)} read as ${asYears ? 'years in the EL program' : 'dates'}` };
}

export function groupValues(values: SisValue[]): { fresh: SisValue[]; notMatched: SisValue[]; matched: SisValue[] } {
  const fresh = values.filter((v) => v.new);
  const rest = values.filter((v) => !v.new);
  return { fresh, notMatched: rest.filter((v) => !v.guess), matched: rest.filter((v) => Boolean(v.guess)) };
}

export function studentsByMeaning(values: SisValue[], meanings: SisMeaning[]): { totals: { key: string; label: string; students: number }[]; none: number } {
  const byMeaning = new Map<string, number>();
  let none = 0;
  for (const v of values) {
    if (v.meaning) byMeaning.set(v.meaning, (byMeaning.get(v.meaning) ?? 0) + v.students);
    else none += v.students;
  }
  return {
    totals: meanings.map((m) => ({ key: m.key, label: m.key === 'ignore' ? 'Ignored' : m.label, students: byMeaning.get(m.key) ?? 0 })),
    none,
  };
}

function patchField(district: SisDistrict, key: string, patch: (field: SisField) => SisField): SisDistrict {
  if (!district.fields.some((f) => f.key === key)) return district;
  return { ...district, fields: district.fields.map((f) => (f.key === key ? patch(f) : f)) };
}

export function withColumnChoice(district: SisDistrict, key: string, column: string): SisDistrict {
  return patchField(district, key, (f) => ({ ...f, choice: { column, notInSis: false }, values: undefined }));
}

export function withNotInSis(district: SisDistrict, key: string): SisDistrict {
  return patchField(district, key, (f) => ({ ...f, choice: { notInSis: true }, values: undefined }));
}

export function withMeaning(district: SisDistrict, key: string, valueKey: string, meaning: string | null): SisDistrict {
  return patchField(district, key, (f) => ({ ...f, values: f.values?.map((v) => (v.key === valueKey ? { ...v, meaning: meaning ?? undefined } : v)) }));
}
